/**
 * Product preview.
 *
 * The core of the landing page: a static recommendation result next to the three
 * rules that produced it. It exists because "we score candidates" means nothing
 * to a first-time visitor, while a ranked list with a first pick, two backups and
 * a commute estimate reads immediately.
 *
 * Every number here is sample content: no request is issued, no real event,
 * participant or recommendation is read, and the panel says so out loud so the
 * mock cannot be mistaken for live data.
 */

import { Clock, Gauge, ListOrdered, MapPin, ShieldCheck, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../ui/badge'

interface PreviewCandidate {
  /** Stable key for the list. */
  id: string
  rank: string
  /** Name of the suggested area. */
  name: string
  /** Score and commute, shown as machine readable sample values. */
  score: string
  commute: string
  /** Short reason the candidate ranked where it did. */
  note: string
  tags: string[]
  /** The first pick is visually marked as well as labelled. */
  primary: boolean
}

export function RecommendationPreview() {
  const { t } = useTranslation()

  const scoreLabel = t('home.previewScoreLabel')
  const commuteLabel = t('home.previewCommuteLabel')

  const candidates: PreviewCandidate[] = [
    {
      id: 'peoples-square',
      rank: '01',
      name: t('home.previewCandidateOne'),
      score: '92',
      commute: '31',
      note: t('home.previewCandidateOneNote'),
      tags: [t('activity.dining'), t('activity.cinema'), t('home.previewTagMetro')],
      primary: true,
    },
    {
      id: 'xujiahui',
      rank: '02',
      name: t('home.previewCandidateTwo'),
      score: '86',
      commute: '36',
      note: t('home.previewCandidateTwoNote'),
      tags: [t('activity.dining'), t('activity.cinema')],
      primary: false,
    },
    {
      id: 'wujiaochang',
      rank: '03',
      name: t('home.previewCandidateThree'),
      score: '78',
      commute: '44',
      note: t('home.previewCandidateThreeNote'),
      tags: [t('activity.dining')],
      primary: false,
    },
  ]

  const rules = [
    {
      icon: ShieldCheck,
      title: t('home.previewLogicHardTitle'),
      body: t('home.previewLogicHardBody'),
    },
    {
      icon: Sparkles,
      title: t('home.previewLogicPreferenceTitle'),
      body: t('home.previewLogicPreferenceBody'),
    },
    {
      icon: ListOrdered,
      title: t('home.previewLogicConfirmTitle'),
      body: t('home.previewLogicConfirmBody'),
    },
  ]

  return (
    <section className="landing-section" aria-labelledby="landing-preview-title">
      <div className="landing-container flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h2
            id="landing-preview-title"
            className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
          >
            {t('home.previewTitle')}
          </h2>
          <p className="text-muted-foreground max-w-2xl text-sm">{t('home.previewSubtitle')}</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          {/* Sample result panel. */}
          <div className="border-border bg-card flex flex-col gap-2.5 rounded-2xl border p-3.5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="truncate text-sm font-semibold">{t('home.previewEventTitle')}</p>
                <p className="text-muted-foreground text-xs">{t('home.previewParticipants')}</p>
              </div>
              <Badge variant="outline" className="shrink-0">
                {t('home.previewSampleBadge')}
              </Badge>
            </div>

            <ol className="flex flex-col gap-1.5">
              {candidates.map((candidate) => (
                <li
                  key={candidate.id}
                  className={
                    candidate.primary
                      ? 'border-primary/50 bg-primary/5 flex flex-col gap-1.5 rounded-2xl border p-2.5'
                      : 'border-border flex flex-col gap-1.5 rounded-2xl border p-2.5'
                  }
                >
                  {/*
                    The two metric pairs drop below the area name on a phone and
                    sit beside it from `sm` up. Kept beside it at 320px the name
                    is squeezed into a two-line column and the row grows past its
                    own card, so the left block keeps `min-w-0` and the name
                    truncates rather than widening anything.
                  */}
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="text-muted-foreground shrink-0 text-xs font-medium tabular-nums">
                        {candidate.rank}
                      </span>
                      <span className="flex min-w-0 items-center gap-1 text-sm font-semibold">
                        <MapPin className="text-primary size-3.5 shrink-0" aria-hidden />
                        <span className="truncate">{candidate.name}</span>
                      </span>
                      {candidate.primary ? (
                        <Badge variant="secondary" className="shrink-0">
                          {t('home.previewFirstPick')}
                        </Badge>
                      ) : null}
                    </div>

                    {/*
                      Label and number share a line: stacked, the two pairs cost
                      a second row of height on every candidate for no extra
                      information.
                    */}
                    <dl className="flex shrink-0 flex-wrap items-baseline gap-x-4 gap-y-1 sm:text-right">
                      <div className="flex items-baseline gap-1">
                        <dt className="text-muted-foreground flex items-center gap-1 text-[11px]">
                          <Gauge className="size-3" aria-hidden />
                          {scoreLabel}
                        </dt>
                        <dd className="text-sm font-semibold tabular-nums">{candidate.score}</dd>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <dt className="text-muted-foreground flex items-center gap-1 text-[11px]">
                          <Clock className="size-3" aria-hidden />
                          {commuteLabel}
                        </dt>
                        <dd className="text-sm font-semibold tabular-nums">
                          {t('home.previewCommuteValue', { minutes: candidate.commute })}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <p className="text-muted-foreground text-xs">{candidate.note}</p>

                  <ul className="flex flex-wrap gap-1.5">
                    {candidate.tags.map((tag) => (
                      <li
                        key={tag}
                        className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[11px]"
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>

            {/* Decorative: the real action lives in the closing call to action. */}
            <span
              className="bg-primary text-primary-foreground inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium"
              aria-hidden
            >
              {t('home.previewVote')}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold">{t('home.previewLogicTitle')}</h3>
            <ul className="flex flex-col gap-1.5">
              {rules.map((rule) => (
                <li key={rule.title} className="border-border bg-card flex gap-3 rounded-2xl border p-2.5">
                  <span className="bg-accent text-accent-foreground flex size-9 shrink-0 items-center justify-center rounded-xl">
                    <rule.icon className="size-4" aria-hidden />
                  </span>
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="text-sm font-semibold">{rule.title}</p>
                    <p className="text-muted-foreground text-sm">{rule.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
